import { useFocusEffect } from "expo-router";
import { useCallback, useRef, type RefObject } from "react";
import {
    Platform,
    type ScrollView,
    type View,
    type NativeSyntheticEvent,
    type NativeScrollEvent
} from "react-native";

import { useContextVisibility } from "@/components/visit/ContextVisibility";

type Bounds = { y: number; height: number };
function measure(node: View | ScrollView): Promise<Bounds | null> {
    if (Platform.OS === "web") {
        const element = node as unknown as { getBoundingClientRect?: () => DOMRect };
        const rect = element.getBoundingClientRect?.();
        return Promise.resolve(rect ? { y: rect.top, height: rect.height } : null);
    }
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(null), 150);
        const native = "getNativeScrollRef" in node ? node.getNativeScrollRef() : node;
        native?.measureInWindow((_x, y, _width, height) => {
            clearTimeout(timer);
            resolve({ y, height });
        });
    });
}

/** Preserve a question's screen position, rather than its obsolete content offset. */
export function useHintScrollAnchor(
    scroll: RefObject<ScrollView | null>,
    questions: RefObject<Map<string, View>>
) {
    const { registerScrollAnchor } = useContextVisibility();
    const offset = useRef(0);
    useFocusEffect(
        useCallback(
            () =>
                registerScrollAnchor(async () => {
                    const scroller = scroll.current;
                    if (!scroller) {
                        return null;
                    }
                    const viewport =
                        Platform.OS === "web"
                            ? await measure(scroller.getScrollableNode())
                            : await measure(scroller);
                    if (!viewport) {
                        return null;
                    }
                    const measured = await Promise.all(
                        [...questions.current.entries()].map(async ([id, node]) => ({
                            id,
                            node,
                            bounds: await measure(node)
                        }))
                    );
                    const rows = measured
                        .filter(
                            (row): row is typeof row & { bounds: Bounds } =>
                                !!row.bounds && row.bounds.height > 0
                        )
                        .sort((a, b) => a.bounds.y - b.bounds.y);
                    // Prefer the first heading in view; a partially hidden preceding hint
                    // may disappear entirely, so its interior is not a stable anchor.
                    const anchor =
                        rows.find(
                            (row) =>
                                row.bounds.y >= viewport.y - 1 &&
                                row.bounds.y < viewport.y + viewport.height
                        ) ?? rows.filter((row) => row.bounds.y < viewport.y).at(-1);
                    if (!anchor) {
                        return null;
                    }
                    return () => {
                        const restore = async () => {
                            if (
                                scroll.current !== scroller ||
                                questions.current.get(anchor.id) !== anchor.node
                            ) {
                                return;
                            }
                            const after = await measure(anchor.node);
                            if (!after) {
                                return;
                            }
                            // Collapsing content can clamp the scroll offset at the bottom.
                            // Measure from the actual offset after layout, not the old one.
                            const currentOffset =
                                Platform.OS === "web"
                                    ? (scroller.getScrollableNode() as HTMLElement).scrollTop
                                    : offset.current;
                            const y = Math.max(0, currentOffset + after.y - anchor.bounds.y);
                            scroller.scrollTo({ y, animated: false });
                            offset.current = y;
                        };
                        if (Platform.OS === "web") {
                            void restore();
                        } else {
                            requestAnimationFrame(() => void restore());
                        }
                    };
                }),
            [registerScrollAnchor, scroll, questions]
        )
    );
    const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
        offset.current = event.nativeEvent.contentOffset.y;
    }, []);
    const scrollToView = useCallback(
        async (target: View, inset = 24) => {
            const scroller = scroll.current;
            if (!scroller) {
                return;
            }
            const [viewport, destination] = await Promise.all([
                measure(Platform.OS === "web" ? scroller.getScrollableNode() : scroller),
                measure(target)
            ]);
            if (!viewport || !destination || scroll.current !== scroller) {
                return;
            }
            const currentOffset =
                Platform.OS === "web"
                    ? (scroller.getScrollableNode() as HTMLElement).scrollTop
                    : offset.current;
            scroller.scrollTo({
                y: Math.max(0, currentOffset + destination.y - viewport.y - inset),
                animated: true
            });
        },
        [scroll]
    );
    return { onScroll, scrollToView };
}
